import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Star,
  Quote,
  BookOpen,
  Trash2,
  StickyNote,
  Save,
  X,
} from 'lucide-react';
import { formatLocalDate } from '../utils/date';

/**
 * Favorites — personal collection of starred reflections and sermons,
 * with per-item notes.
 */
const Favorites = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reflections, setReflections] = useState([]);
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/favorites?kind=reflection`),
        axios.get(`${API}/favorites?kind=sermon`),
      ]);
      setReflections(r1.data || []);
      setSermons(r2.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load favorites');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleDelete = async (fav) => {
    if (!window.confirm('Remove this from your collection?')) return;
    try {
      await axios.delete(`${API}/favorites/${fav.id}`);
      toast.success('Removed');
      fetchAll();
    } catch {
      toast.error('Could not remove');
    }
  };

  const openNoteEditor = (fav) => {
    setEditingNoteId(fav.id);
    setNoteDraft(fav.notes || '');
  };

  const saveNote = async (fav) => {
    try {
      await axios.patch(`${API}/favorites/${fav.id}`, { notes: noteDraft });
      setEditingNoteId(null);
      setNoteDraft('');
      toast.success('Note saved');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save note');
    }
  };

  const renderNoteBlock = (fav) => {
    if (editingNoteId === fav.id) {
      return (
        <div className="mt-3 space-y-2">
          <Textarea
            rows={4}
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            maxLength={4000}
            placeholder="What stood out?"
            data-testid={`fav-note-input-${fav.id}`}
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setEditingNoteId(null);
                setNoteDraft('');
              }}
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => saveNote(fav)}
              className="bg-[#2B6B7F]"
              data-testid={`fav-note-save-${fav.id}`}
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              Save
            </Button>
          </div>
        </div>
      );
    }

    if (fav.notes) {
      return (
        <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5 text-xs text-amber-900 font-semibold">
              <StickyNote className="w-3.5 h-3.5" />
              Your note
            </div>
            <button
              onClick={() => openNoteEditor(fav)}
              className="text-xs text-amber-900 hover:underline"
              data-testid={`fav-note-edit-${fav.id}`}
            >
              Edit
            </button>
          </div>
          <p className="text-sm text-amber-900 whitespace-pre-wrap">{fav.notes}</p>
        </div>
      );
    }
    return (
      <button
        onClick={() => openNoteEditor(fav)}
        className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#2B6B7F]"
        data-testid={`fav-note-add-${fav.id}`}
      >
        <StickyNote className="w-3.5 h-3.5" />
        Add a note
      </button>
    );
  };

  const emptyState = (icon, text, cta) => (
    <div className="text-center py-16" data-testid="favorites-empty">
      <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
        {icon}
      </div>
      <p className="text-slate-600 mb-4">{text}</p>
      {cta}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/40 to-white">
      <Navigation />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4 text-[#2B6B7F]"
          data-testid="favorites-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
            <Star className="w-6 h-6 text-white fill-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading text-slate-800">Your Collection</h1>
            <p className="text-sm text-slate-500">Reflections and sermons you've starred</p>
          </div>
        </div>

        <Tabs defaultValue="reflections">
          <TabsList className="grid grid-cols-2 w-full max-w-sm">
            <TabsTrigger value="reflections" data-testid="fav-tab-reflections">
              <Quote className="w-4 h-4 mr-1" />
              Reflections ({reflections.length})
            </TabsTrigger>
            <TabsTrigger value="sermons" data-testid="fav-tab-sermons">
              <BookOpen className="w-4 h-4 mr-1" />
              Sermons ({sermons.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reflections" className="mt-4 space-y-3">
            {loading ? (
              <p className="text-center text-slate-500 py-8">Loading…</p>
            ) : reflections.length === 0 ? (
              emptyState(
                <Quote className="w-7 h-7 text-amber-500" />,
                "No starred reflections yet — tap the ⭐ on any reflection to save it here.",
                <Button onClick={() => navigate('/reflections')} className="bg-amber-500 hover:bg-amber-600">
                  Browse reflections
                </Button>,
              )
            ) : (
              reflections.map((fav) => {
                const p = fav.item_payload || {};
                return (
                  <Card
                    key={fav.id}
                    className="border-l-4 border-l-amber-400"
                    data-testid={`fav-reflection-${fav.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg font-heading text-slate-800">
                          {p.title || fav.item_id}
                        </CardTitle>
                        <button
                          onClick={() => handleDelete(fav)}
                          className="text-slate-400 hover:text-red-500 flex-shrink-0"
                          data-testid={`fav-remove-${fav.id}`}
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Saved {formatLocalDate(fav.created_at, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </CardHeader>
                    <CardContent>
                      {p.body && (
                        <div className="flex gap-2">
                          <Quote className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                          <p className="text-slate-700 leading-relaxed">{p.body}</p>
                        </div>
                      )}
                      {renderNoteBlock(fav)}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="sermons" className="mt-4 space-y-3">
            {loading ? (
              <p className="text-center text-slate-500 py-8">Loading…</p>
            ) : sermons.length === 0 ? (
              emptyState(
                <BookOpen className="w-7 h-7 text-amber-500" />,
                "No starred sermons yet — tap the ⭐ on any sermon week to save it here.",
                <Button onClick={() => navigate('/sermon')} className="bg-amber-500 hover:bg-amber-600">
                  Open sermon notes
                </Button>,
              )
            ) : (
              sermons.map((fav) => {
                const p = fav.item_payload || {};
                return (
                  <Card
                    key={fav.id}
                    className="border-l-4 border-l-amber-400"
                    data-testid={`fav-sermon-${fav.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <CardTitle className="text-lg font-heading text-slate-800 break-words">
                            {p.passage || fav.item_id}
                          </CardTitle>
                          {p.week_start_date && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              Week of {formatLocalDate(p.week_start_date, { month: 'long', day: 'numeric', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDelete(fav)}
                          className="text-slate-400 hover:text-red-500 flex-shrink-0"
                          data-testid={`fav-remove-${fav.id}`}
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {p.scripture_text && (
                        <p className="text-sm text-slate-600 italic mb-2 line-clamp-3">
                          {p.scripture_text}
                        </p>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/sermon?week=${encodeURIComponent(p.week_start_date || '')}`)}
                        data-testid={`fav-open-sermon-${fav.id}`}
                      >
                        Open sermon
                      </Button>
                      {renderNoteBlock(fav)}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Favorites;
